import { useDeferredValue, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { BottleList } from '../components/BottleList';
import { useResource } from '../hooks/useResource';
import { api, ApiError, errorMessage } from '../lib/api';
import { matchesText } from '../lib/search';

/** A friend's bar, read-only. */
export function FriendBarPage() {
  const { id = '' } = useParams();
  const friendId = Number(id);
  const { data, error, loading, reload } = useResource(`friend-bar:${id}`, (signal) => api.friendBar(friendId, signal));
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query.trim());

  const visible = useMemo(
    () => (data?.bottles ?? []).filter((bottle) => matchesText([bottle.name, bottle.brand, bottle.variant], deferredQuery)),
    [data, deferredQuery],
  );

  const backLink = (
    <Link className="back-link" to={`/friends/${id}`}>
      ← Collection
    </Link>
  );

  if (!data) {
    if (loading) return <p className="page-status">Loading bar…</p>;
    const notFriend = error instanceof ApiError && (error.status === 404 || error.status === 400);
    return (
      <section className="page-status" role="alert">
        {backLink}
        <p className={notFriend ? undefined : 'error-text'}>{notFriend ? "That person isn't one of your friends." : errorMessage(error)}</p>
        {!notFriend && (
          <button type="button" className="btn btn--primary" onClick={reload}>
            Try again
          </button>
        )}
      </section>
    );
  }

  const name = data.friend.name ?? data.friend.email;
  return (
    <section className="collection-page">
      {backLink}
      <header className="page-header">
        <div>
          <h1>{name}'s bar</h1>
          <p className="muted">
            {data.bottles.length} {data.bottles.length === 1 ? 'bottle' : 'bottles'}
          </p>
        </div>
      </header>
      {data.bottles.length === 0 ? (
        <div className="empty-state">
          <p>{name} hasn't added any bottles yet.</p>
        </div>
      ) : (
        <>
          <div className="filters">
            <input
              type="search"
              className="filters__search"
              placeholder="Search name, brand or vintage"
              aria-label={`Search ${name}'s bar`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {visible.length === 0 ? <p className="hint">No bottles match.</p> : <BottleList bottles={visible} />}
        </>
      )}
    </section>
  );
}
